import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useQuery } from '@apollo/client';

import {
  ButtonModel,
  IconRegularCheckmark,
  IconRegularClose,
} from '@notino/react-styleguide';
import {
  Roles,
  GetCustomerBenefitsQuery,
  GetUserLoyaltyProgressQuery,
} from '@notino/shared/definitions/types';

import { LOGIN_URL } from '@constants';

import { MY_NOTINO_BENEFIT_PAGE, REGISTER_URL } from '../../constants';
import getCustomerBenefitsQuery from '../../queries/getCustomerBenefits.graphql';
import {
  determineStatus,
  trackLoyaltyModalOpen,
} from '../../trackLoyaltyModal';
import { BaseLoyaltyModal } from '../BaseLoyaltyModal';
import { StyledButton } from '../styled';

import { messages } from './messages';
import getUserLoyaltyProgressQuery from './queries/getUserLoyaltyProgress.graphql';
import {
  LoginWrapper,
  LoginLink,
  StepProgressCircle,
  StepWrapper,
  FooterContent,
} from './styled';
import { trackLoyaltyCTA } from './trackLoyaltyCTA';

enum HowToGetDiscountSteps {
  Register = 'registerStep',
  Newsletter = 'newsletterStep',
  ChooseBrandAndCategory = 'chooseBrandAndCategoryStep',
}

export type FullfilledSteps = Record<HowToGetDiscountSteps, boolean>;

export const HowToLoyaltyModal: React.FC<{ timing: number }> = ({ timing }) => {
  const { formatMessage } = useIntl();
  const { data: getUser, loading: getUserLoading } =
    useQuery<GetUserLoyaltyProgressQuery>(getUserLoyaltyProgressQuery);

  const { data: checkCustomerBenefitsResult } =
    useQuery<GetCustomerBenefitsQuery>(getCustomerBenefitsQuery);

  const brandDiscountsSelected =
    checkCustomerBenefitsResult?.customerBenefits?.brandDiscounts?.length > 0;
  const categoryDiscountsSelected =
    checkCustomerBenefitsResult?.customerBenefits?.categoryDiscounts?.length >
    0;

  const fulfilledSteps = React.useMemo<FullfilledSteps>(
    () => ({
      [HowToGetDiscountSteps.Register]:
        getUser && getUser.user ? getUser.user.role !== Roles.Anonymous : false,
      [HowToGetDiscountSteps.Newsletter]: getUser?.isUserSubscribed ?? false,
      [HowToGetDiscountSteps.ChooseBrandAndCategory]: Boolean(
        brandDiscountsSelected || categoryDiscountsSelected
      ),
    }),
    [brandDiscountsSelected, categoryDiscountsSelected, getUser]
  );

  const onButtonClick = () => {
    if (!fulfilledSteps.registerStep) {
      trackLoyaltyCTA('register');
      location.href = REGISTER_URL;
    } else {
      trackLoyaltyCTA('change_settings');
      location.href = MY_NOTINO_BENEFIT_PAGE;
    }
  };

  const handleLoginClick = () => {
    trackLoyaltyCTA('log_in');
  };

  React.useEffect(() => {
    if (!getUserLoading) {
      trackLoyaltyModalOpen(determineStatus(fulfilledSteps), timing);
    }
  }, [fulfilledSteps, timing, getUserLoading]);

  return (
    <BaseLoyaltyModal
      headerMessage={formatMessage(messages.howToGetLoyaltyDiscountHeader)}
      footerContent={
        !getUserLoading && (
          <FooterContent>
            <StyledButton
              onClick={onButtonClick}
              buttonStyle={ButtonModel.Styles.primary}
            >
              {fulfilledSteps.registerStep ? (
                <FormattedMessage {...messages.setUpNow} />
              ) : (
                <FormattedMessage {...messages.registerNow} />
              )}
            </StyledButton>
            {!fulfilledSteps.registerStep && (
              <LoginWrapper>
                <FormattedMessage {...messages.alreadyHaveAccount} />
                <LoginLink href={LOGIN_URL} onClick={handleLoginClick}>
                  <FormattedMessage {...messages.logIn} />
                </LoginLink>
              </LoginWrapper>
            )}
          </FooterContent>
        )
      }
    >
      <div>
        {Object.values(HowToGetDiscountSteps).map((step) => (
          <StepWrapper key={step}>
            <StepProgressCircle isFulfilled={fulfilledSteps[step]}>
              {fulfilledSteps[step] ? (
                <IconRegularCheckmark
                  color="icon.inverse"
                  width="12px"
                  height="12px"
                />
              ) : (
                <IconRegularClose
                  color="icon.primary"
                  width="12px"
                  height="12px"
                />
              )}
            </StepProgressCircle>
            <FormattedMessage {...messages[step]} />
          </StepWrapper>
        ))}
      </div>
    </BaseLoyaltyModal>
  );
};
